<template>
  <BaseModal :show="show" @close="$emit('close')">
    <template #title>
      Powiąż produkt
    </template>
    
    <div class="space-y-3">
      <!-- Search Input -->
      <div class="relative">
        <div class="absolute left-3 top-1/2 -translate-y-1/2">
          <font-awesome-icon icon="search" class="text-slate-400" />
        </div>
        <input 
          ref="inputEl" 
          v-model.trim="search" 
          type="text" 
          class="w-full h-10 pl-10 pr-4 border border-blue-200 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" 
          placeholder="Szukaj produktu po nazwie" 
        />
      </div>

      <!-- Results -->
      <div class="max-h-80 overflow-y-auto">
        <div v-if="loading" class="text-center py-6">
          <div class="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p class="text-slate-600 mt-2 text-sm">Wyszukiwanie...</p>
        </div>
        <div v-else-if="search.trim().length < 2" class="text-center py-6 text-slate-500">
          <p class="text-sm">Zacznij pisać, aby wyszukać</p>
        </div>
        <div v-else-if="resultsHtml" class="space-y-1">
          <div v-html="resultsHtml" @click="onResultsClick" class="product-results"></div>
        </div>
        <div v-else class="text-center py-6 text-slate-500">
          <p class="text-sm">Nie znaleziono produktów</p>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import BaseModal from '../../../shared/components/BaseModal.vue';
import { productApi } from '../services/products.api';
import { useApiConfig } from '../../../composables/useApi';

const props = defineProps<{ show: boolean }>();
const emit = defineEmits<{ (e:'close'):void; (e:'select', item:{ id:number; name:string; description?:string|null }):void }>();

const search = ref('');
const inputEl = ref<HTMLInputElement|null>(null);
const loading = ref(false);
const resultsHtml = ref('');
const api = useApiConfig();

function debounce<F extends (...args: any[]) => any>(fn: F, delay = 300) {
  let t: any;
  return (...args: Parameters<F>) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
}

const debouncedSearch = debounce(async () => {
  const q = search.value.trim();
  if (q.length < 2) {
    resultsHtml.value = '';
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const html = await productApi.searchWithConfig(api, q);
    resultsHtml.value = html || '';
    console.debug('Product search: html length', resultsHtml.value.length);
  } catch (e) {
    console.error('Product search failed', e);
    resultsHtml.value = '';
  } finally {
    loading.value = false;
  }
}, 300);

watch(search, () => debouncedSearch());
watch(() => props.show, async (v) => {
  if (v) {
    await nextTick();
    inputEl.value?.focus();
    if (search.value) debouncedSearch();
  }
});

function onResultsClick(e: MouseEvent) {
  const target = e.target as HTMLElement;
  let link = target.closest('[data-product-id]') as HTMLElement | null;
  if (!link) {
    // fallback to common selectors used in legacy tpl
    link = target.closest('[data-id]') as HTMLElement | null;
  }
  if (!link) return;
  const idAttr = link.getAttribute('data-product-id') || link.getAttribute('data-id') || '';
  const id = Number(idAttr);
  const name = (link.getAttribute('data-product-name') || link.getAttribute('data-name') || link.textContent || '').trim();
  const description = link.getAttribute('data-product-description') || link.getAttribute('data-description') || null;
  emit('select', { id, name, description });
}
</script>

<style>
/* Global styles for product results from API */
.product-results [data-product-id],
.product-results [data-id] {
  @apply flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer;
}

.product-results [data-product-id] .product-image,
.product-results [data-id] .product-image {
  @apply w-10 h-10 rounded-lg object-cover bg-slate-100 flex-shrink-0;
}

.product-results [data-product-id] .product-details,
.product-results [data-id] .product-details {
  @apply flex-1 min-w-0;
}

.product-results [data-product-id] .product-name,
.product-results [data-id] .product-name {
  @apply font-medium text-slate-800 text-sm leading-tight;
}

.product-results [data-product-id] .product-description,
.product-results [data-id] .product-description {
  @apply text-xs text-slate-500 mt-0.5 line-clamp-2;
}

.product-results [data-product-id] .product-button,
.product-results [data-id] .product-button {
  @apply px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors;
}
</style>
